import { useEffect, useState } from 'react';
import { MenuItem } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomAutoComplete,
  CustomTextField,
} from '../../../common/components';
import { useUnits } from '../../../hooks/useUnits';
import {
  INITIAL_INVENTORY_ARTICLE_STATE,
  VALIDATION_RULES,
} from '../constants/inventoryArticlesConstants';
import { validateInventoryArticle } from '../utils/inventoryArticlesUtils';

const InventoryArticleDialog = ({
  open,
  onClose,
  editMode,
  selectedArticle,
  onCreate,
  onUpdate,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState(INITIAL_INVENTORY_ARTICLE_STATE);
  const [errors, setErrors] = useState({});
  const { data: units = [] } = useUnits();

  useEffect(() => {
    if (open) {
      if (editMode && selectedArticle) {
        setFormData({
          uuid: selectedArticle.uuid,
          code: selectedArticle.code || '',
          name: selectedArticle.name || '',
          description: selectedArticle.description || '',
          uuid_unit: selectedArticle.uuid_unit || '',
          external_code: selectedArticle.external_code || '',
          is_active: selectedArticle.is_active ?? true,
        });
      } else {
        setFormData(INITIAL_INVENTORY_ARTICLE_STATE);
      }
      setErrors({});
    }
  }, [open, editMode, selectedArticle]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateInventoryArticle(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      uuid_unit: formData.uuid_unit || null,
      external_code: formData.external_code?.trim() || null,
    };

    try {
      if (editMode && formData.uuid) {
        await onUpdate(formData.uuid, {
          ...payload,
          is_active: formData.is_active,
        });
      } else {
        await onCreate(payload);
      }
      onClose();
    } catch (error) {
      console.error('Error al guardar el artículo:', error);
    }
  };

  return (
    <DialogContainer open={open} onClose={onClose} size="medium">
      <DialogTitle>
        {editMode ? 'Editar artículo' : 'Nuevo artículo'}
      </DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CustomTextField
          label="Código"
          name="code"
          value={formData.code}
          onChange={handleChange}
          required
          error={Boolean(errors.code)}
          helperText={errors.code}
          inputProps={{ maxLength: VALIDATION_RULES.CODE_MAX_LENGTH }}
        />

        <CustomTextField
          label="Nombre"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          error={Boolean(errors.name)}
          helperText={errors.name}
          inputProps={{ maxLength: VALIDATION_RULES.NAME_MAX_LENGTH }}
        />

        <CustomTextField
          label="Descripción"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          minRows={2}
        />

        <CustomAutoComplete
          label="Unidad de medida"
          name="uuid_unit"
          value={formData.uuid_unit}
          onChange={handleChange}
        >
          <MenuItem value="">Sin unidad</MenuItem>
          {units.map((unit) => (
            <MenuItem key={unit.uuid} value={unit.uuid}>
              {unit.description}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomTextField
          label="Código externo"
          name="external_code"
          value={formData.external_code}
          onChange={handleChange}
          error={Boolean(errors.external_code)}
          helperText={errors.external_code}
          inputProps={{ maxLength: VALIDATION_RULES.EXTERNAL_CODE_MAX_LENGTH }}
        />

        {editMode && (
          <CustomAutoComplete
            label="Estado"
            name="is_active"
            value={formData.is_active ? 'active' : 'inactive'}
            onChange={(event) => {
              const isActive = event.target.value === 'active';
              setFormData((prev) => ({ ...prev, is_active: isActive }));
            }}
          >
            <MenuItem value="active">Activo</MenuItem>
            <MenuItem value="inactive">Inactivo</MenuItem>
          </CustomAutoComplete>
        )}
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveText={editMode ? 'Actualizar' : 'Crear'}
        isSaving={isSubmitting}
      />
    </DialogContainer>
  );
};

export default InventoryArticleDialog;
